"use client";

import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import hooks from "@/hooks";
import { Form } from "@/components/ui/form";
import FormFields from "../Fields";
import Buttons from "@/components/core/Buttons";
import { AddressData, CreateSalonData, CreateSalonFormSchema } from "./schema";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { MapPin, Pencil, Plus, Trash2 } from "lucide-react";
import PhoneInput from "react-phone-input-2";
import { Label } from "@/components/ui/label";
import AddressModal from "@/components/layouts/SalonModals/updateadrees";

interface ISalonResponse {
  data?: ISalonResponse;
  id?: string;
  name?: string;
  social_reason?: string;
  email?: string;
  document?: string;
  cpfCnpj?: string;
  whatsapp?: string;
  secondary_whatsapp?: string;
  addresses?: AddressData[];
  salon_address?: any;
}

interface ICreateSalonFormProps {
  user?: { id: string };
  ownerId?: string | null;
  onSuccess?: () => void;
}

const CreateSalonForm = ({
  user,
  ownerId,
  onSuccess,
}: ICreateSalonFormProps) => {
  const { toast } = useToast();
  const isEditing = !!ownerId;

  const { data: rawData, isLoading: isLoadingData } = hooks.useGetSalonById(
    ownerId || "",
    // @ts-expect-error - useGetSalonById pode não aceitar options dependendo da versão, mas precisamos do enabled
    {
      enabled: isEditing,
    },
  );

  const form = useForm<CreateSalonData>({
    resolver: zodResolver(CreateSalonFormSchema),
    defaultValues: {
      name: "",
      email: "",
      cpfCnpj: "",
      whatsapp: "+55",
      secondary_whatsapp: "+55",
      addresses: [],
    },
  });

  const { fields, append, update, remove } = useFieldArray({
    control: form.control,
    name: "addresses",
  });

  useEffect(() => {
    if (isEditing && rawData) {
      const response = rawData as unknown as ISalonResponse;
      const apiData = response.data || response;

      if (!apiData) return;

      let formattedAddresses: AddressData[] = [];

      if (Array.isArray(apiData.addresses)) {
        formattedAddresses = apiData.addresses.map((addr) => ({
          ...addr,
          complement: addr.complement || "",
        }));
      } else if (apiData.salon_address) {
        const source = Array.isArray(apiData.salon_address)
          ? apiData.salon_address
          : [apiData.salon_address];

        formattedAddresses = source.map(
          (item: { address?: AddressData } & any) => {
            const addr = item.address || item;
            return {
              street: (addr.street as string) || "",
              number: (addr.number as string) || "",
              district: (addr.district as string) || "",
              city: (addr.city as string) || "",
              state: (addr.state as string) || "",
              zip_code: (addr.zip_code as string) || "",
              complement: (addr.complement as string) || "",
              country: (addr.country as string) || "Brasil",
            };
          },
        );
      }

      form.reset({
        name: apiData.name || apiData.social_reason || "",
        email: apiData.email || "",
        cpfCnpj: apiData.document || apiData.cpfCnpj || "",
        whatsapp: apiData.whatsapp || "+55",
        secondary_whatsapp:
          apiData.secondary_whatsapp && apiData.secondary_whatsapp !== "null"
            ? apiData.secondary_whatsapp
            : "+55",
        addresses: formattedAddresses,
      });
    }
  }, [rawData, isEditing, form]);

  const createSalonService = hooks.useCreateSalon({
    onSuccess: () => {
      toast({
        title: "Sucesso!",
        description: "Salão criado.",
        variant: "success",
      });
      onSuccess?.();
    },
    onError: (e: any) =>
      toast({
        title: "Não foi possível criar o salão",
        description: e.message,
        variant: "warning",
      }),
  });

  const updateSalonService = hooks.useUpdateSalon({
    onSuccess: () => {
      toast({
        title: "Sucesso!",
        description: "Salão atualizado.",
        variant: "success",
      });
      onSuccess?.();
    },
    onError: (e: any) =>
      toast({
        title: "Não foi possível atualizar o salão",
        description: e.message,
        variant: "warning",
      }),
  });

  async function onSubmit(data: CreateSalonData) {
    if (!data.addresses || data.addresses.length === 0) {
      toast({
        title: "Endereço obrigatório",
        description: "Cadastre pelo menos um endereço",
        variant: "warning",
      });
      setAddressModalOpen(true);
      return;
    }

    const payload = {
      name: data.name,
      email: data.email,
      document: data.cpfCnpj,
      whatsapp: data.whatsapp,
      secondary_whatsapp:
        data.secondary_whatsapp && data.secondary_whatsapp.length > 2
          ? data.secondary_whatsapp
          : undefined,
      addresses: data.addresses,
    };

    if (isEditing && ownerId) {
      await updateSalonService.mutateAsync({
        id: ownerId,
        data: payload as any,
      });
    } else {
      if (!user?.id) return;
      await createSalonService.mutateAsync({
        user_id: user.id,
        ...payload,
      });
    }
  }

  const [isAddressModalOpen, setAddressModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingAddress, setEditingAddress] = useState<
    AddressData | undefined
  >();
  const [isEditingAddress, setIsEditingAddress] = useState(false);

  useEffect(() => {
    if (!isEditing && fields.length === 0) {
      setAddressModalOpen(true);
    }
  }, [isEditing, fields.length]);

  const openCreateAddressModal = () => {
    setIsEditingAddress(false);
    setEditingIndex(null);
    setEditingAddress(undefined);
    setAddressModalOpen(true);
  };

  const openEditAddressModal = (address: AddressData, index: number) => {
    setIsEditingAddress(true);
    setEditingIndex(index);
    setEditingAddress(address);
    setAddressModalOpen(true);
  };

  const handleSaveAddress = (address: AddressData) => {
    if (isEditingAddress && editingIndex !== null) {
      update(editingIndex, address);
    } else {
      append(address);
    }
    setAddressModalOpen(false);
    setEditingAddress(undefined);
    setEditingIndex(null);
    setIsEditingAddress(false);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="w-full flex flex-col gap-y-6 bg-white p-6 rounded-3xl"
      >
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-xl font-bold text-gray-800">
            {isEditing ? "Editar Empresa" : "Nova Empresa"}
          </h2>
          {isEditing && isLoadingData && (
            <span className="text-sm text-gray-500 animate-pulse">
              Carregando dados...
            </span>
          )}
        </div>

        <FormFields.InputFormField
          form={form}
          formName="cpfCnpj"
          label="CPF/CNPJ"
          placeholder="___.___.___-__"
          mask="document"
          disabled={isLoadingData}
        />

        <FormFields.InputFormField
          form={form}
          formName="name"
          label="Nome do salão"
          disabled={isLoadingData}
        />

        <FormFields.InputFormField
          form={form}
          formName="email"
          label="E-mail do salão"
          disabled={isLoadingData}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>WhatsApp</Label>
            <PhoneInput
              country="br"
              value={form.watch("whatsapp")}
              onChange={(v) => form.setValue("whatsapp", v)}
              inputStyle={{ width: "100%", height: 40 }}
              disabled={isLoadingData}
            />
          </div>

          <div>
            <Label>WhatsApp secundário</Label>
            <PhoneInput
              country="br"
              value={form.watch("secondary_whatsapp")}
              onChange={(v) => form.setValue("secondary_whatsapp", v)}
              inputStyle={{ width: "100%", height: 40 }}
              disabled={isLoadingData}
            />
          </div>
        </div>

        <Button
          type="button"
          onClick={openCreateAddressModal}
          className="w-full"
          disabled={isLoadingData}
        >
          <Plus className="mr-2 h-4 w-4" />
          Adicionar endereço
        </Button>

        {fields.length > 0 ? (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {fields.map((field, index) => (
                  <tr key={field.id} className="border-t">
                    <td className="p-3">
                      <MapPin className="inline mr-1 h-3 w-3 text-gray-500" />
                      {field.street}, {field.number}
                    </td>
                    <td className="p-3">
                      {field.city} / {field.state}
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            openEditAddressModal(field as AddressData, index)
                          }
                        >
                          <Pencil className="h-4 w-4 text-blue-600" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => remove(index)}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="border border-dashed rounded-lg p-6 text-center text-muted-foreground bg-gray-50">
            {isLoadingData ? "Carregando..." : "Nenhum endereço adicionado"}
          </div>
        )}

        <Buttons.DefaultButton
          type="submit"
          title={isEditing ? "Salvar alterações" : "Criar salão"}
          isLoading={
            createSalonService.isPending ||
            updateSalonService.isPending ||
            (isEditing && isLoadingData)
          }
        />
      </form>

      {isAddressModalOpen && (
        <AddressModal
          isOpen={isAddressModalOpen}
          onOpenChange={setAddressModalOpen}
          onSave={handleSaveAddress}
          initialValues={editingAddress}
          isEditing={isEditingAddress}
        />
      )}
    </Form>
  );
};

export default CreateSalonForm;
